import {
  ChangeDetectorRef,
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import Croppie from 'croppie';
import { ToastService } from 'src/app/services/toast.service';

@Component({
    selector: 'app-content-image-upload-modal',
    templateUrl: './content-image-upload-modal.component.html',
    styleUrls: ['./content-image-upload-modal.component.scss'],
    standalone: false
})
export class ContentImageUploadModalComponent implements OnInit, AfterViewInit {
  @ViewChild('croppieElement') croppieElement: ElementRef;
  contentImageForm: FormGroup;
  croppieInstance: Croppie;
  aiGenerateModal: boolean = false;
  selectedImage: string | null = null; // Full image URL
  croppedImage: string | null = null; // Cropped result
  fullImage: string; // Uncropped image for upload

  constructor(
    private formBuilder: FormBuilder,
    private activeModal: NgbActiveModal,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.contentImageForm = this.formBuilder.group({
      image: [this.selectedImage || null, Validators.required],
    });

    if (
      this.selectedImage?.includes('http') ||
      this.selectedImage?.includes('https')
    ) {
      this.urlToBase64(this.selectedImage).then((result) => {
        this.fullImage = result;
      });
    } else if (this.selectedImage?.includes('data:image')) {
      this.fullImage = this.selectedImage;
    }
  }

  ngAfterViewInit(): void {
    this.initializeCroppie();
  }

  private initializeCroppie(): void {
    this.croppieInstance = new Croppie(this.croppieElement.nativeElement, {
      viewport: { width: 325, height: 490, type: 'square' },
      boundary: { width: 350, height: 500 }, // Slightly larger for zoom/pan
      showZoomer: true,
      enableOrientation: true,
    });

    if (this.selectedImage) {
      this.croppieInstance.bind({ url: this.selectedImage });
    }
  }

  urlToBase64(url: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.onload = function () {
        const reader = new FileReader();
        reader.onloadend = function () {
          resolve(reader.result as string);};
        reader.onerror = function (error) {
          reject(error);
        };
        reader.readAsDataURL(xhr.response);};
      xhr.onerror = function (error) {
        reject(error);
      };
      xhr.open('GET', url);
      xhr.responseType = 'blob';
      xhr.send();
    });
  }

  isImageBase64(data) {
    if (typeof data !== 'string') return false;

    const imageBase64Regex =
      /^data:image\/(jpe?g|png|gif);base64,[A-Za-z0-9+/=]+$/;

    return imageBase64Regex.test(data);
  }

  handleFileInput(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      this.selectedImage = reader.result as string;
      this.fullImage = this.selectedImage;

      if (!this.croppieInstance) {
        this.initializeCroppie();
      }
      this.contentImageForm.patchValue({ image: this.selectedImage });

      this.croppieInstance.bind({ url: this.selectedImage }).catch((err) => {
        console.error('Croppie bind error:', err);
        this.toastService.showSuccess("That picture couldn't be loaded. Try a different image.", 'danger');
      });
    
          this.cdr.detectChanges();};
    reader.readAsDataURL(file);
  }

  uploadImage(): void {
    if (!this.croppieInstance || !this.selectedImage) {
      this.toastService.showSuccess('Choose a picture first.', 'danger');
      return;
    }

    this.croppieInstance
      .result('base64')
      .then((croppedImage) => {
        this.croppedImage = croppedImage;
        this.contentImageForm.patchValue({ image: this.croppedImage });
        this.closeWithResult();
      })
      .catch((err) => {
        console.error('Croppie result error:', err);
        this.toastService.showSuccess("We couldn't crop the picture. Please try again.", 'danger');
      });
  }

  closeModal(): void {
    if (this.aiGenerateModal) {
      this.activeModal.close({
        aiImageCancel: true
      });
    } else {
      this.activeModal.close();
    }
  }

  private closeWithResult(): void {
    this.activeModal.close({
      content_image_base64: this.croppedImage,
      regular_image: this.fullImage
    });
  }
}
